import React from "react";
import { Checkbox, Label, Slider, makeStyles, tokens } from "@fluentui/react-components";
import type { PaletteOption } from "../visualConfig/types";
import { PALETTE_COLORS } from "../visualConfig/palettes";
import { type ChartStyleSectionProps, useSliderStyles } from "./shared";

interface HeatmapStyleProps extends ChartStyleSectionProps {
  /** Current palette — used to show available heatmap colors */
  palette: PaletteOption;
}

const useHeatmapStyles = makeStyles({
  swatchGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  swatch: {
    width: "28px",
    height: "28px",
    borderRadius: "6px",
    cursor: "pointer",
    border: `2px solid transparent`,
    transition: "border-color 0.15s ease",
    ":hover": {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
    },
  },
});

const HeatmapStyle: React.FC<HeatmapStyleProps> = ({ chartStyle: cs, onStyleChange, palette }) => {
  const sliderStyles = useSliderStyles();
  const styles = useHeatmapStyles();
  const colors = PALETTE_COLORS[palette] ?? PALETTE_COLORS.default;
  const selectedIndex = cs.heatmapColorIndex ?? 0;

  return (
    <>
      <Label size="small" weight="semibold">Heatmap color</Label>
      <div className={styles.swatchGrid}>
        {colors.map((color, idx) => (
          <div
            key={idx}
            className={styles.swatch}
            style={{
              backgroundColor: color,
              borderColor: idx === selectedIndex ? tokens.colorBrandStroke1 : undefined,
            }}
            onClick={() => onStyleChange({ heatmapColorIndex: idx })}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onStyleChange({ heatmapColorIndex: idx });
              }
            }}
            title={`Color ${idx + 1}`}
            aria-label={`Select heatmap color ${idx + 1}`}
          />
        ))}
      </div>
      <Checkbox
        label="Show color slider"
        checked={cs.showSlider ?? false}
        onChange={(_, data) => onStyleChange({ showSlider: !!data.checked })}
      />
      <div className={sliderStyles.sliderField}>
        <Label size="small">Cell Radius</Label>
        <div className={sliderStyles.sliderRow}>
          <Slider
            min={0} max={10} step={1}
            value={cs.cellRadius ?? 0}
            onChange={(_, data) => onStyleChange({ cellRadius: data.value })}
            style={{ flex: 1 }}
          />
          <span className={sliderStyles.sliderValue}>{cs.cellRadius ?? 0}px</span>
        </div>
      </div>
    </>
  );
};

export default HeatmapStyle;